import { atom, getDefaultStore } from 'jotai'
import { getParents } from '@/lib/event'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import { store } from '@/services/store.service'
import { parse } from '@nostr/tools/nip27'
import { noteEncode, naddrEncode } from '@nostr/tools/nip19'
import type { ProfilePointer, AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { NostrEvent } from '@nostr/tools'
import { createPromptSession, type PromptSession } from '@/lib/prompt-api'

const cache = new Map<string, ReturnType<typeof atom<string | undefined>>>()

export const emptySummaryAtom = atom<string | undefined>(undefined)

export function getSummaryAtomIfExists(key: string) {
  return cache.get(key)
}

function getOrCreateSummaryAtom(key: string) {
  let atom_ = cache.get(key)
  if (!atom_) {
    atom_ = atom<string | undefined>(undefined)
    cache.set(key, atom_)
  }
  return atom_
}

const jotaiStore = getDefaultStore()

export function saveSummary(key: string, summary: string) {
  jotaiStore.set(getOrCreateSummaryAtom(key), summary)
}

export async function getSummary(event: NostrEvent): Promise<string | undefined> {
  const sessionPromise = createPromptSession(
    `You are given a Nostr conversation thread. Notes are formatted as "[role] author name: content", where role can be "root", "ancestor", "parent", "current", "root/current" or "reply N". A literal "[missing context]" line means a note we could not fetch. References to other notes inside a content are rendered as "<<author name: content>>" and may be nested; references to profiles are replaced by the short name of the profile. Produce a very concise, short readable markdown summary of the whole conversation, written in the same language as the notes. Don't summarize each note, rather summarize the overall vibe of the full thread. Respond with only the summary.`
  )
  let session: PromptSession | undefined
  try {
    const context = await buildThreadContext(event)
    session = await sessionPromise
    if (!session) return
    return await session.prompt(context)
  } catch (err) {
    console.error('failed to summarize thread', err)
  } finally {
    if (!session && sessionPromise) session = await sessionPromise.catch(() => undefined)
    session?.destroy()
  }
}

async function buildThreadContext(event: NostrEvent) {
  const seen = new Set<string>()
  const line = async (evt: { pubkey: string; content: string }, role: string) =>
    `[${role}] ${(await loadNostrUser(evt.pubkey)).shortName}: ${await resolveReferences(evt.content, seen)}`

  const lines: string[] = []

  // climb from current up to root via the local store
  const chain: NostrEvent[] = []
  const climbedIds = new Set<string>()
  let current: NostrEvent | undefined = event
  let missing = false
  while (current) {
    const parentPtr = getParents(current).find((p) => 'id' in p)
    if (!parentPtr) break
    if (climbedIds.has(parentPtr.id)) {
      missing = true
      break
    }
    climbedIds.add(parentPtr.id)
    const [parent] = await store.queryEvents({ ids: [parentPtr.id] }, 1)
    if (!parent) {
      missing = true
      break
    }
    chain.push(parent)
    current = parent
  }

  if (missing) lines.push('[missing context]')

  if (chain.length === 0) {
    lines.push(await line(event, missing ? 'current' : 'root/current'))
  } else {
    const topDown = [...chain].reverse()
    for (let i = 0; i < topDown.length; i++) {
      const isTop = i === 0
      const isBottom = i === topDown.length - 1
      const role = isTop && !missing ? 'root' : isBottom ? 'parent' : 'ancestor'
      lines.push(await line(topDown[i], role))
    }
    lines.push(await line(event, 'current'))
  }

  const replies = await store.queryEvents({
    '#e': [event.id],
    kinds: [1, 1111],
    limit: 5
  })
  for (const reply of replies) {
    lines.push(await line(reply, `reply ${lines.filter((l) => l.startsWith('[reply')).length + 1}`))
  }

  return lines.join('\n\n')
}

async function resolveReferences(content: string, seen: Set<string>): Promise<string> {
  let out = ''
  for (const block of parse(content)) {
    if (block.type === 'text') {
      out += block.text
    } else if (block.type === 'reference') {
      out += await renderReference(block.pointer, seen)
    } else if (block.type === 'emoji') {
      out += `:${block.shortcode}:`
    } else if (block.type === 'hashtag') {
      out += `#${block.value}`
    } else {
      out += block.url
    }
  }
  return out
}

async function renderReference(
  pointer: ProfilePointer | AddressPointer | EventPointer,
  seen: Set<string>
): Promise<string> {
  if ('id' in pointer) {
    if (seen.has(pointer.id)) return ''
    const [evt] = await store.queryEvents({ ids: [pointer.id] }, 1)
    if (!evt) return noteEncode(pointer.id)
    return renderNote(evt, seen)
  }
  if ('identifier' in pointer) {
    const [evt] = await store.queryEvents(
      { authors: [pointer.pubkey], kinds: [pointer.kind], '#d': [pointer.identifier] },
      1
    )
    if (!evt) return naddrEncode(pointer)
    return renderNote(evt, seen)
  }
  return (await loadNostrUser(pointer.pubkey)).shortName
}

async function renderNote(
  evt: { id: string; pubkey: string; content: string },
  seen: Set<string>
): Promise<string> {
  if (seen.has(evt.id)) return ''
  seen.add(evt.id)
  return `<<${(await loadNostrUser(evt.pubkey)).shortName}: ${await resolveReferences(evt.content, seen)}>>`
}
